import React from "react";
import {
  AbsoluteFill,
  useCurrentFrame,
  useVideoConfig,
  spring,
  interpolate,
  Sequence,
  Audio,
  Img,
  staticFile,
} from "remotion";
import {SPRINGS, FONT_SCALE, SAFE_ZONES} from "../presets/editing-styles";
import {CLAMP, FONT_STACK, IDV} from "../presets/idv";
import {Noise} from "../components/dimus/Noise";
import {Vignette} from "../components/dimus/Vignette";
import {FilmGrain} from "../components/dimus/FilmGrain";
import {GradientText} from "../components/dimus/GradientText";
import {SceneTransition} from "../components/dimus/SceneTransition";
import {OrganicLayer} from "../components/overlays/OrganicLayer";

/**
 * CAFÉ COM TEU ROI EP09 — "Relatório Bonito Não Paga Boleto"
 * Editing Style: DYNAMIC (Dark Premium)
 * Profile: @guilhermeribeiro.ai
 * Audio: ElevenLabs TTS per-scene (voz Guilherme)
 */

const ORANGE = "#FF6B35";
const GREEN = IDV.secondary;
const RED = IDV.error;
const AMBER = "#f5ad43";
const WHITE = "#f5f5f7";
const MUTED = "#a3a3a3";

const SZ = SAFE_ZONES.universal;

const SCENES = [
  {dur: 78, file: "audio/cafe_ep09/cafe_ep09_c01.mp3", dir: "right" as const},
  {dur: 355, file: "audio/cafe_ep09/cafe_ep09_c02.mp3", dir: "left" as const},
  {dur: 369, file: "audio/cafe_ep09/cafe_ep09_c03.mp3", dir: "up" as const},
  {dur: 336, file: "audio/cafe_ep09/cafe_ep09_c04.mp3", dir: "right" as const},
  {dur: 134, file: "audio/cafe_ep09/cafe_ep09_c05.mp3", dir: "up" as const},
];

export const CAFE_EP09_TOTAL = SCENES.reduce((s, sc) => s + sc.dur, 0);

// ─── Fake metric bar ──────────────────────────────────────────────────
const MetricBar: React.FC<{
  label: string; value: string; color: string; delay: number; strikethrough?: boolean;
}> = ({label, value, color, delay, strikethrough}) => {
  const frame = useCurrentFrame();
  const {fps} = useVideoConfig();
  const p = spring({frame: frame - delay, fps, config: SPRINGS.snappy});
  const strikeP = strikethrough ? spring({frame: frame - delay - 40, fps, config: SPRINGS.smooth}) : 0;

  if (frame < delay) return null;
  return (
    <div style={{
      opacity: p,
      transform: `translateX(${interpolate(p, [0, 1], [40, 0], CLAMP)}px)`,
      display: "flex", justifyContent: "space-between", alignItems: "center",
      padding: "18px 28px", borderRadius: 16, width: "100%",
      background: "rgba(255,255,255,0.04)",
      border: "1px solid rgba(255,255,255,0.06)",
      position: "relative",
    }}>
      <div style={{fontSize: FONT_SCALE.body.min, fontWeight: 500, color: MUTED, fontFamily: FONT_STACK}}>
        {label}
      </div>
      <div style={{fontSize: FONT_SCALE.h2.min, fontWeight: 800, color, fontFamily: FONT_STACK}}>
        {value}
      </div>
      {strikethrough && (
        <div style={{
          position: "absolute", left: "5%", top: "50%",
          width: `${strikeP * 90}%`, height: 3,
          background: RED, borderRadius: 2,
          transform: "translateY(-50%)",
        }} />
      )}
    </div>
  );
};

// ─── SCENE 1: HOOK ────────────────────────────────────────────────────
const S1: React.FC = () => (
  <AbsoluteFill style={{justifyContent: "center", alignItems: "center", padding: `${SZ.top}px ${SZ.left}px ${SZ.bottom}px`}}>
    <Audio src={staticFile(SCENES[0].file)} volume={0.9} />
    <div style={{display: "flex", flexDirection: "column", alignItems: "center", gap: 8}}>
      <div style={{
        padding: "8px 24px", borderRadius: 999,
        background: "rgba(255,107,53,0.15)", border: "1px solid rgba(255,107,53,0.3)",
        fontSize: FONT_SCALE.label.min, fontWeight: 600, color: ORANGE, fontFamily: FONT_STACK,
      }}>
        EPISÓDIO 09
      </div>
      <GradientText text="CAFÉ COM TEU ROI" colors={[ORANGE, AMBER]} fontSize={FONT_SCALE.h1.min} springProfile="bouncy" />
    </div>
  </AbsoluteFill>
);

// ─── SCENE 2: PROBLEMA — relatório bonito ────────────────────────────
const S2: React.FC = () => {
  const frame = useCurrentFrame();
  const {fps} = useVideoConfig();
  const titleP = spring({frame: frame - 10, fps, config: SPRINGS.heavy});

  return (
    <OrganicLayer intensity="subtle" seed="cafe-ep09">
    <AbsoluteFill style={{justifyContent: "center", alignItems: "center", padding: `${SZ.top}px ${SZ.left}px ${SZ.bottom}px`, flexDirection: "column", gap: 24}}>
      <Audio src={staticFile(SCENES[1].file)} volume={0.9} />
      <div style={{
        opacity: titleP, fontSize: FONT_SCALE.h2.min, fontWeight: 700,
        color: WHITE, fontFamily: FONT_STACK, textAlign: "center", lineHeight: 1.2,
      }}>
        Relatório bonito.{"\n"}Cheio de número.
      </div>
      <div style={{width: "88%", display: "flex", flexDirection: "column", gap: 14}}>
        <MetricBar label="Impressões" value="2.4M" color={AMBER} delay={60} strikethrough />
        <MetricBar label="Alcance" value="890K" color={AMBER} delay={80} strikethrough />
        <MetricBar label="Cliques" value="12.5K" color={AMBER} delay={100} strikethrough />
      </div>
      <div style={{
        opacity: spring({frame: frame - 200, fps, config: SPRINGS.smooth}),
        fontSize: FONT_SCALE.body.min, fontWeight: 700, color: RED,
        fontFamily: FONT_STACK, textAlign: "center",
      }}>
        Nenhum te diz se tá vendendo.
      </div>
    </AbsoluteFill>
    </OrganicLayer>
  );
};

// ─── SCENE 3: EVIDÊNCIA — métricas mentirosas ────────────────────────
const S3: React.FC = () => {
  const frame = useCurrentFrame();
  const {fps} = useVideoConfig();
  const p1 = spring({frame: frame - 5, fps, config: SPRINGS.heavy});
  const p2 = spring({frame: frame - 80, fps, config: SPRINGS.smooth});
  const p3 = spring({frame: frame - 180, fps, config: SPRINGS.bouncy});

  return (
    <AbsoluteFill style={{justifyContent: "center", alignItems: "flex-start", padding: `${SZ.top}px ${SZ.left}px ${SZ.bottom}px`, flexDirection: "column", gap: 24}}>
      <Audio src={staticFile(SCENES[2].file)} volume={0.9} />
      <div style={{
        opacity: p1, fontSize: FONT_SCALE.h2.min, fontWeight: 800,
        color: WHITE, fontFamily: FONT_STACK, lineHeight: 1.2,
      }}>
        Impressão não paga boleto.
      </div>
      <div style={{
        opacity: p2, fontSize: FONT_SCALE.body.min, fontWeight: 500,
        color: MUTED, fontFamily: FONT_STACK, lineHeight: 1.4, maxWidth: "92%",
      }}>
        Alcance não fecha contrato. E clique é a métrica mais mentirosa que existe.
      </div>
      <div style={{
        opacity: p3,
        transform: `scale(${interpolate(p3, [0, 1], [0.85, 1], CLAMP)})`,
        padding: "20px 36px", borderRadius: 20,
        background: "rgba(248,113,113,0.1)", border: "1px solid rgba(248,113,113,0.25)",
        fontSize: FONT_SCALE.body.min, fontWeight: 700, color: RED,
        fontFamily: FONT_STACK, textAlign: "center",
      }}>
        Curiosidade não deposita{"\n"}na tua conta.
      </div>
    </AbsoluteFill>
  );
};

// ─── SCENE 4: VIRADA — 3 números que importam ────────────────────────
const S4: React.FC = () => {
  const frame = useCurrentFrame();
  const {fps} = useVideoConfig();

  return (
    <AbsoluteFill style={{justifyContent: "center", alignItems: "center", padding: `${SZ.top}px ${SZ.left}px ${SZ.bottom}px`, flexDirection: "column", gap: 20}}>
      <Audio src={staticFile(SCENES[3].file)} volume={0.9} />
      <div style={{
        opacity: spring({frame: frame - 5, fps, config: SPRINGS.smooth}),
        fontSize: FONT_SCALE.body.min, fontWeight: 500, color: MUTED,
        fontFamily: FONT_STACK, textAlign: "center",
      }}>
        Pede só 3 números:
      </div>
      <div style={{width: "88%", display: "flex", flexDirection: "column", gap: 14}}>
        <MetricBar label="Custo por lead qualificado" value="CPL" color={GREEN} delay={40} />
        <MetricBar label="Taxa de conversão" value="CVR" color={GREEN} delay={80} />
        <MetricBar label="Retorno sobre investimento" value="ROI" color={GREEN} delay={120} />
      </div>
      <div style={{
        opacity: spring({frame: frame - 220, fps, config: SPRINGS.bouncy}),
        fontSize: FONT_SCALE.body.min, fontWeight: 800, color: AMBER,
        fontFamily: FONT_STACK, textAlign: "center", marginTop: 8,
      }}>
        Se não souberem de cabeça,{"\n"}o relatório tá escondendo um buraco.
      </div>
    </AbsoluteFill>
  );
};

// ─── SCENE 5: CTA ────────────────────────────────────────────────────
const S5: React.FC = () => {
  const frame = useCurrentFrame();
  const {fps} = useVideoConfig();
  const profileP = spring({frame: frame - 8, fps, config: SPRINGS.snappy});
  const btnP = spring({frame: frame - 40, fps, config: SPRINGS.bouncy});
  const pulse = frame > 80 ? 1 + Math.sin((frame - 80) * 0.08) * 0.02 : 1;

  return (
    <AbsoluteFill style={{justifyContent: "center", alignItems: "center", gap: 20, flexDirection: "column", padding: `${SZ.top}px ${SZ.left}px ${SZ.bottom}px`}}>
      <Audio src={staticFile(SCENES[4].file)} volume={0.9} />
      <div style={{
        opacity: profileP, fontSize: FONT_SCALE.body.min, fontWeight: 500,
        color: MUTED, fontFamily: FONT_STACK, textAlign: "center", lineHeight: 1.3,
      }}>
        Amanhã: como ler um relatório{"\n"}em 30 segundos.
      </div>
      <div style={{
        opacity: profileP, display: "flex", alignItems: "center", gap: 16,
        transform: `translateY(${interpolate(profileP, [0, 1], [20, 0], CLAMP)}px)`,
      }}>
        <div style={{
          width: 64, height: 64, borderRadius: "50%", overflow: "hidden",
          border: `3px solid ${ORANGE}`, boxShadow: `0 0 20px rgba(255,107,53,0.3)`,
        }}>
          <Img src={staticFile("assets/profiles/guilhermeribeiro-ai.jpg")}
            style={{width: "100%", height: "100%", objectFit: "cover"}} />
        </div>
        <div>
          <div style={{fontSize: FONT_SCALE.body.min, fontWeight: 700, color: WHITE, fontFamily: FONT_STACK}}>@guilhermeribeiro.ai</div>
          <div style={{fontSize: FONT_SCALE.label.min, fontWeight: 400, color: MUTED, fontFamily: FONT_STACK}}>Café com teu ROI</div>
        </div>
      </div>
      <div style={{
        opacity: btnP,
        transform: `translateY(${interpolate(btnP, [0, 1], [30, 0], CLAMP)}px) scale(${interpolate(btnP, [0, 1], [0.5, 1], CLAMP) * pulse})`,
        padding: "18px 52px", borderRadius: 999, background: ORANGE,
        fontSize: FONT_SCALE.cta.max, fontWeight: 700, color: "#000", fontFamily: FONT_STACK,
        boxShadow: `0 0 40px rgba(255,107,53,0.4)`,
      }}>
        SEGUE PRA NÃO PERDER
      </div>
    </AbsoluteFill>
  );
};

// ─── MAIN ─────────────────────────────────────────────────────────────
export const CafeEp09: React.FC = () => {
  let offset = 0;
  return (
    <AbsoluteFill style={{backgroundColor: "#000"}}>
      <div style={{position: "absolute", inset: 0, background: `radial-gradient(ellipse at 50% 35%, #1a0a05, #000 70%)`}} />
      <Noise opacity={0.02} />
      <FilmGrain opacity={0.04} />
      <Vignette intensity={0.7} />

      {SCENES.map(({dur, dir}, i) => {
        const from = offset;
        offset += dur;
        const Comp = [S1, S2, S3, S4, S5][i];
        return (
          <Sequence key={i} from={from} durationInFrames={dur}>
            <SceneFade duration={dur}>
              <SceneTransition type="in" direction={dir} springProfile="smooth">
                <Comp />
              </SceneTransition>
            </SceneFade>
          </Sequence>
        );
      })}
    </AbsoluteFill>
  );
};

const SceneFade: React.FC<{duration: number; children: React.ReactNode}> = ({duration, children}) => {
  const frame = useCurrentFrame();
  const fadeIn = interpolate(frame, [0, 8], [0, 1], CLAMP);
  const fadeOut = interpolate(frame, [duration - 10, duration], [1, 0], CLAMP);
  return <AbsoluteFill style={{opacity: Math.min(fadeIn, fadeOut)}}>{children}</AbsoluteFill>;
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
